/**
 * Product Personalisation Component
 * Gère la personnalisation de produit avec saisie de texte, couleurs et emojis
 */

class ProductPersonalisation {
  constructor() {
    this.init();
  }

  init() {
    const nextElement = document.querySelector(
      "[data-component-personalisation]",
    );

    this.cleanup();
    this.releaseElements();

    this.el = nextElement;
    if (!this.el) return;

    this.eventsAbortController = new AbortController();

    this.initElements();
    this.initValues();
    this.initEvents();
    this.initPlaceholderAnimation();

    this.syncPersonalisationState();

    this.initialResetTimeout = setTimeout(() => {
      this.initialResetTimeout = undefined;
      this.resetFields();
    }, 100);

    this.disabledInableBtn();
  }

  cleanup() {
    this.buttonStateObserver?.disconnect();
    this.buttonStateObserver = undefined;

    this.eventsAbortController?.abort();
    this.eventsAbortController = undefined;

    this.unbindContentImageLoadListeners();
    this.hidePictoLimitWarning();
    this.errorMsg?.setAttribute("hidden", "");

    this.variantChangeUnsubscriber?.();
    this.variantChangeUnsubscriber = undefined;

    clearTimeout(this.initialResetTimeout);
    clearTimeout(this.errorMessageTimeout);
    clearTimeout(this.buttonStateTimeout);
    clearTimeout(this.pictoWarningTimeout);
    clearTimeout(this.resetFieldsTimeout);
    clearTimeout(this.placeholderAnimationTimeout);

    this.initialResetTimeout = undefined;
    this.errorMessageTimeout = undefined;
    this.buttonStateTimeout = undefined;
    this.pictoWarningTimeout = undefined;
    this.resetFieldsTimeout = undefined;
    this.placeholderAnimationTimeout = undefined;
  }

  releaseElements() {
    this.el = undefined;
    this.productInfo = undefined;
    this.open = undefined;
    this.cancel = undefined;
    this.accordion = undefined;
    this.content = undefined;
    this.input = undefined;
    this.nbr = undefined;
    this.quantitySelect = undefined;
    this.quantitySelectWrapper = undefined;
    this.packUi = undefined;
    this.tabsContainer = undefined;
    this.packPreview = undefined;
    this.defaultPreview = undefined;
    this.summaryEl = undefined;
    this.pictoBtns = [];
    this.colors = [];
    this.colorLabel = undefined;
    this.errorMsg = undefined;
    this.pictoWarning = undefined;
    this.pictoWarningText = undefined;
    this.addToCartBtn = undefined;
    this.contentImageLoadBound = false;
    this.contentImageLoadHandler = undefined;
    this.placeholderExamples = [];
    this.placeholderAnimationStopped = false;
  }

  initElements() {
    this.productInfo =
      this.el.closest?.("product-info") ||
      document.querySelector("product-info");
    this.open = this.el.querySelector("[data-component-personalisation-open]");
    this.cancel = this.el.querySelector(
      "[data-component-personalisation-cancel]",
    );
    this.accordion = this.el.querySelector(
      "[data-component-personalisation-accordion]",
    );
    this.content = this.el.querySelector(
      "[data-component-personalisation-content]",
    );
    this.input = this.el.querySelector(
      "[data-component-personalisation-input]",
    );
    this.nbr = this.el.querySelector("[data-component-personalisation-nbr]");
    this.quantitySelect = this.el.querySelector(
      "[data-component-personalisation-quantity]",
    );
    this.quantitySelectWrapper = this.el.querySelector(
      "[data-component-personalisation-quantity-wrapper]",
    );
    this.packUi = this.el.querySelector(
      "[data-component-personalisation-pack-ui]",
    );
    this.tabsContainer = this.el.querySelector(
      "[data-component-personalisation-tabs]",
    );
    this.packPreview = this.el.querySelector(
      "[data-component-personalisation-pack-preview]",
    );
    this.defaultPreview = this.el.querySelector(
      "[data-component-personalisation-default-preview]",
    );
    this.summaryEl = this.el.querySelector(
      "[data-component-personalisation-summary]",
    );
    this.pictoBtns = [
      ...this.el.querySelectorAll("[data-component-personalisation-picto]"),
    ];
    this.colors = [
      ...this.el.querySelectorAll("[data-component-personalisation-color]"),
    ];
    this.colorLabel = this.el.querySelector(
      "[data-component-personalisation-color-label]",
    );
    this.errorMsg = this.el.querySelector(
      "[data-component-personalisation-error]",
    );
    this.pictoWarning = this.el.querySelector(
      "[data-component-personalisation-picto-warning]",
    );
    this.pictoWarningText = this.el.querySelector(
      "[data-component-personalisation-picto-warning-text]",
    );
    this.addToCartBtn =
      document.querySelector(".product-form__submit") ||
      document.querySelector(".js-product-add-to-cart") ||
      document.querySelector("[data-add-to-cart]");
  }

  getProductForm() {
    return (
      document.querySelector(".product-form") ||
      document.querySelector(".js-main-product-form") ||
      document.querySelector("[data-product-form]") ||
      this.el.closest("form")
    );
  }

  getSelectedVariantId() {
    const form = this.getProductForm();
    const idInput =
      form?.querySelector('input[name="id"]:not([disabled])') ||
      document.querySelector('input[name="id"]:not([disabled])');
    return idInput?.value || this.el.dataset.selectedVariantId || null;
  }

  parsePackComposition() {
    try {
      const raw = this.el.dataset.packComposition;
      this.packComposition = raw ? JSON.parse(raw) : {};
    } catch {
      this.packComposition = {};
    }
  }

  getPackTabLabel(index) {
    const template =
      theme?.personalizationStrings?.packTabLabel || "Article {{ index }}";
    return template.replace("{{ index }}", index);
  }

  isTabComplete(item) {
    return !!(item?.text?.length && item?.colorName);
  }

  getCompletedCount() {
    if (!this.isPackMode) return 0;
    return this.packItems.filter((item) => this.isTabComplete(item)).length;
  }

  getEmbroideryQuantity() {
    if (this.isPackMode) return this.getCompletedCount();
    return this.quantityToEmbroider;
  }

  formatMoney(cents) {
    if (
      typeof theme !== "undefined" &&
      theme.Currency &&
      typeof theme.Currency.formatMoney === "function"
    ) {
      return theme.Currency.formatMoney(
        cents,
        theme.settings?.currencyCodeEnabled
          ? theme.settings.moneyWithCurrencyFormat
          : theme.settings.moneyFormat,
      );
    }
    return `${(cents / 100).toFixed(2)}`;
  }

  showPackUI(show) {
    if (this.packUi) this.packUi.hidden = !show;
    if (this.defaultPreview) this.defaultPreview.hidden = show;
    if (this.quantitySelectWrapper) {
      this.quantitySelectWrapper.hidden = show || this.unitCount <= 1;
    }
    if (this.summaryEl && !show) {
      this.summaryEl.hidden = true;
      this.summaryEl.textContent = "";
    }
  }

  initPackMode() {
    this.parsePackComposition();
    const variantId = this.getSelectedVariantId();
    const items = variantId ? this.packComposition[variantId] : null;
    this.isPackMode = Array.isArray(items) && items.length > 0;

    if (!this.isPackMode) {
      this.packItems = [];
      this.activeTabIndex = 0;
      this.completedCount = 0;
      this.showPackUI(false);
      if (this.tabsContainer) this.tabsContainer.innerHTML = "";
      this.updateCancelButtonLabel();
      return;
    }

    this.packItems = items.map((item, i) => ({
      index: i + 1,
      ean: item.EAN || item.ean || "",
      url: item.URL || item.url || "",
      text: "",
      colorId: null,
      colorName: null,
    }));
    this.activeTabIndex = 0;
    this.completedCount = 0;
    this.showPackUI(true);
    this.buildPackTabs();
    this.loadTabState(0);
    this.updatePriceSummary();
    this.updateCancelButtonLabel();
  }

  updateCancelButtonLabel() {
    if (!this.cancel) return;

    const btnText = this.cancel.querySelector(".btn-text");
    if (!btnText) return;

    if (this.isPackMode) {
      btnText.textContent =
        theme?.personalizationStrings?.cancelPackItem ||
        "Annuler cette broderie";
    } else {
      const defaultLabel = this.cancel.dataset.cancelLabelDefault;
      if (defaultLabel) btnText.textContent = defaultLabel;
    }
  }

  cancelCurrentPackItem() {
    if (!this.isPackMode || !this.packItems[this.activeTabIndex]) return;

    const item = this.packItems[this.activeTabIndex];
    item.text = "";
    item.colorId = null;
    item.colorName = null;

    if (this.input) this.input.value = "";
    this.inputValide = "";

    if (this.colors.length > 0) {
      this.colors.forEach((colorInput) => {
        colorInput.checked = false;
      });
    }
    this.selectedColor = { id: null, name: null };
    if (this.colorLabel) this.colorLabel.textContent = "";
    if (this.nbr) this.nbr.innerHTML = "0";
    this.nbrOfCaracters = 0;

    this.refreshPackState();
  }

  buildPackTabs() {
    if (!this.tabsContainer) return;

    this.tabsContainer.innerHTML = "";

    this.packItems.forEach((item, index) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "product-personalisation__tab";
      btn.dataset.tabIndex = String(index);
      btn.setAttribute("role", "tab");
      btn.setAttribute(
        "aria-selected",
        index === this.activeTabIndex ? "true" : "false",
      );
      btn.setAttribute("aria-controls", "personalisation-input");

      const label = this.getPackTabLabel(item.index);
      btn.innerHTML = `
        <span class="product-personalisation__tab-thumb">
          <img src="${item.url}" alt="" loading="lazy" width="54" height="78">
          <span class="product-personalisation__tab-check" data-component-personalisation-tab-check hidden aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" width="16" height="16" aria-hidden="true">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clip-rule="evenodd" />
            </svg>
          </span>
        </span>
        <span class="product-personalisation__tab-label">${label}</span>
      `;

      this.tabsContainer.appendChild(btn);
    });

    this.updateTabsUI();
    this.updatePackPreview();
  }

  saveCurrentTabState() {
    if (!this.isPackMode || !this.packItems.length) return;

    const item = this.packItems[this.activeTabIndex];
    if (!item) return;

    item.text = this.inputValide || "";
    item.colorId = this.selectedColor.id;
    item.colorName = this.selectedColor.name;
  }

  loadTabState(index) {
    if (!this.isPackMode || !this.packItems[index]) return;

    this.activeTabIndex = index;
    const item = this.packItems[index];

    this.inputValide = item.text || "";
    if (this.input) this.input.value = this.inputValide;

    this.selectedColor = {
      id: item.colorId || null,
      name: item.colorName || null,
    };

    if (this.colors.length > 0) {
      this.colors.forEach((colorInput) => {
        colorInput.checked = colorInput.dataset.colorId === item.colorId;
      });
    }

    if (this.colorLabel) {
      this.colorLabel.textContent = this.selectedColor.name || "";
    }

    this.checkNbrOfCaracters();
    this.updateTabsUI();
    this.updatePackPreview();
  }

  switchTab(index) {
    if (!this.isPackMode || index === this.activeTabIndex) return;

    this.saveCurrentTabState();
    this.loadTabState(index);
    this.refreshPackState();
  }

  updateTabsUI() {
    if (!this.tabsContainer) return;

    const tabs = this.tabsContainer.querySelectorAll(
      ".product-personalisation__tab",
    );

    tabs.forEach((tab, index) => {
      const item = this.packItems[index];
      const isComplete = this.isTabComplete(item);
      const isActive = index === this.activeTabIndex;

      tab.classList.toggle("is-active", isActive);
      tab.classList.toggle("is-complete", isComplete);
      tab.setAttribute("aria-selected", isActive ? "true" : "false");

      const check = tab.querySelector(
        "[data-component-personalisation-tab-check]",
      );
      if (check) check.hidden = !isComplete;
    });
  }

  updatePackPreview() {
    if (!this.packPreview || !this.isPackMode) return;

    const item = this.packItems[this.activeTabIndex];
    if (!item?.url) {
      this.packPreview.innerHTML = "";
      return;
    }

    this.packPreview.innerHTML = `<img src="${item.url}" alt="" loading="lazy" decoding="async" class="inline" width="200">`;
  }

  updatePriceSummary() {
    if (!this.summaryEl) return;

    const count = this.getEmbroideryQuantity();

    if (!this.isPackMode || count === 0) {
      this.summaryEl.hidden = true;
      this.summaryEl.textContent = "";
    } else {
      const totalCents = Math.round(this.customizationPrice * count);
      const price = this.formatMoney(totalCents);
      const template =
        theme?.personalizationStrings?.packSummary ||
        "{{ count }} article(s) brodé(s) = +{{ price }}";

      this.summaryEl.textContent = template
        .replace("{{ count }}", count)
        .replace("{{ price }}", price);
      this.summaryEl.hidden = false;
    }

    this.updateContentMaxHeight();
  }

  refreshPackState() {
    if (!this.isPackMode) return;

    this.saveCurrentTabState();
    this.completedCount = this.getCompletedCount();
    this.updateTabsUI();
    this.updatePriceSummary();
    this.create();
    this.updateBuyButtonPersonalisationSuffix();
    this.disabledInableBtn();
  }

  appendVariantProperties(customizationOptions) {
    const variantPropertiesContainer = document.querySelector(
      ".variant-properties",
    );
    if (!variantPropertiesContainer) return;

    const allInputs = variantPropertiesContainer.querySelectorAll(
      'input[type="hidden"]',
    );
    for (const input of allInputs) {
      const packChildMatch = input.name.match(
        /properties\[(_pack-child-\d+-sku)\]/,
      );
      if (packChildMatch && input.value) {
        customizationOptions[packChildMatch[1]] = input.value;
      }
      if (input.name === "properties[_date]" && input.value) {
        customizationOptions["_date"] = input.value;
      }
    }
  }

  setPersonalisationState(state) {
    if (!this.productInfo) return;
    this.productInfo.setAttribute("data-personalisation-state", state);
  }

  syncPersonalisationState() {
    const isOpen = !!this.content?.classList.contains("is-open");
    if (!isOpen) {
      this.setPersonalisationState("closed");
    } else {
      const form = this.getProductForm();
      const hasInjectedInputs = !!form?.querySelector(
        ".js-personalisation-input",
      );
      if (hasInjectedInputs) {
        this.setPersonalisationState("active");
      } else {
        this.setPersonalisationState("open");
      }
    }
    this.updateBuyButtonPersonalisationSuffix();
  }

  updateBuyButtonPersonalisationSuffix() {
    const btn =
      this.addToCartBtn ||
      document.querySelector(".product-form__submit") ||
      document.querySelector(".js-product-add-to-cart") ||
      document.querySelector("[data-add-to-cart]");
    const suffix = btn?.querySelector(
      "[data-buy-button-personalisation-suffix]",
    );
    if (!suffix) return;

    const form = this.getProductForm();
    const isActive = !!form?.querySelector(".js-personalisation-input");

    if (!isActive) {
      suffix.setAttribute("hidden", "");
      suffix.classList.add("hidden");
      suffix.classList.remove("inline-flex");
      const amountEl = suffix.querySelector(
        "[data-buy-button-personalisation-amount]",
      );
      if (amountEl) amountEl.textContent = "";
      return;
    }

    const quantity = this.getEmbroideryQuantity();
    const totalCents = Math.round(this.customizationPrice * quantity);
    const fmt = this.formatMoney(totalCents);

    const amountEl = suffix.querySelector(
      "[data-buy-button-personalisation-amount]",
    );
    if (amountEl) amountEl.textContent = `+${fmt}`;

    suffix.removeAttribute("hidden");
    suffix.classList.remove("hidden");
    suffix.classList.add("inline-flex");
  }

  initValues() {
    this.inputValide = "";
    this.nbrOfCaracters = 0;
    this.maxNbr =
      parseInt(this.el.dataset.componentPersonalisationMax, 10) || 13;
    this.selectedColor = {
      id: null,
      name: null,
    };

    this.customizationPrice = parseFloat(
      this.el.dataset.customizationPrice || 0,
    );
    this.customizationVariantId =
      this.el.dataset.customizationVariantId || null;

    this.unitCount = parseInt(this.el.dataset.unitCount, 10) || 1;
    this.quantityToEmbroider = 1;

    this.isPackMode = false;
    this.packComposition = {};
    this.packItems = [];
    this.activeTabIndex = 0;
    this.completedCount = 0;

    this.emojis = this.pictoBtns.map((picto) => {
      return {
        emoji: picto.dataset.pictoEmoji,
        value: parseInt(picto.dataset.pictoNumber, 10) || 1,
      };
    });

    const charClassPattern = "A-Za-z0-9çàâéèêîôûù?\"ïöë!:;.,'´`*'\\s\\-";
    const emojiCode = this.emojis.map((e) => `${e.emoji}`).join("");
    const fullPatternString = `^[${charClassPattern}${emojiCode}]*$`;
    this.regex = new RegExp(fullPatternString, "u");

    this.charClassPattern = new RegExp(`[${charClassPattern}]`, "u");
    const emojiUnicodePoints = this.emojis
      .map((e) => `\\u{${e.emoji.codePointAt(0).toString(16)}}`)
      .join("|");
    this.emojiPattern = new RegExp(emojiUnicodePoints, "ug");

    this.initPackMode();
  }

  initEvents() {
    const eventOptions = { signal: this.eventsAbortController.signal };

    if (this.open)
      this.open.addEventListener(
        "click",
        () => this.openPersonalisation(),
        eventOptions,
      );
    if (this.cancel)
      this.cancel.addEventListener(
        "click",
        () => this.cancelPersonalisation(),
        eventOptions,
      );
    if (this.input) {
      this.input.addEventListener(
        "input",
        () => {
          this.stopPlaceholderAnimation();
          this.addValueInput();
        },
        eventOptions,
      );
      this.input.addEventListener(
        "focus",
        () => this.stopPlaceholderAnimation(),
        eventOptions,
      );
      this.input.addEventListener(
        "change",
        () => {
          if (this.input.value !== "" && this.inputValide === "") {
            this.input.value = "";
            if (this.nbr) {
              this.nbr.innerHTML = "0";
            }
            this.nbrOfCaracters = 0;
          }
        },
        eventOptions,
      );
    }

    if (this.quantitySelect) {
      this.quantitySelect.addEventListener(
        "change",
        () => {
          this.quantityToEmbroider =
            parseInt(this.quantitySelect.value, 10) || 1;
          this.create();
        },
        eventOptions,
      );
    }

    this.pictoBtns.forEach((pictoBtn) => {
      pictoBtn.addEventListener(
        "click",
        () => this.addPicto(pictoBtn),
        eventOptions,
      );
    });

    this.colors.forEach((colorInput) => {
      colorInput.addEventListener(
        "change",
        () => this.selectColor(colorInput),
        eventOptions,
      );
    });

    if (this.tabsContainer) {
      this.tabsContainer.addEventListener(
        "click",
        (event) => {
          const tab = event.target.closest?.(".product-personalisation__tab");
          if (!tab || !this.tabsContainer.contains(tab)) return;

          this.switchTab(parseInt(tab.dataset.tabIndex, 10));
        },
        eventOptions,
      );
    }

    if (this.addToCartBtn) {
      this.observeButtonState();
    }

    if (typeof theme !== "undefined" && theme.pubsub) {
      this.variantChangeUnsubscriber = theme.pubsub.subscribe(
        theme.pubsub.PUB_SUB_EVENTS.variantChange,
        (event) => this.handleVariantChange(event),
      );
    }
  }

  initPlaceholderAnimation() {
    if (!this.input) return;

    this.defaultPlaceholder = this.input.getAttribute("placeholder") || "";
    this.placeholderExamples = (this.input.dataset.placeholderExamples || "")
      .split(",")
      .map((example) => example.trim())
      .filter(Boolean);

    if (!this.placeholderExamples.length) return;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      this.input.placeholder = this.placeholderExamples[0];
      return;
    }

    this.placeholderExampleIndex = 0;
    this.placeholderCharacterIndex = 0;
    this.placeholderIsDeleting = false;
    this.placeholderAnimationStopped = false;
    this.input.placeholder = "";
    this.placeholderAnimationTimeout = setTimeout(
      () => this.animatePlaceholder(),
      500,
    );
  }

  animatePlaceholder() {
    if (
      !this.input ||
      this.placeholderAnimationStopped ||
      this.input.value ||
      !this.placeholderExamples.length
    )
      return;

    const example = this.placeholderExamples[this.placeholderExampleIndex];

    if (this.placeholderIsDeleting) {
      this.placeholderCharacterIndex -= 1;
    } else {
      this.placeholderCharacterIndex += 1;
    }

    this.input.placeholder = example.slice(0, this.placeholderCharacterIndex);

    let delay = this.placeholderIsDeleting ? 45 : 90;

    if (
      !this.placeholderIsDeleting &&
      this.placeholderCharacterIndex === example.length
    ) {
      this.placeholderIsDeleting = true;
      delay = 1400;
    } else if (
      this.placeholderIsDeleting &&
      this.placeholderCharacterIndex === 0
    ) {
      this.placeholderIsDeleting = false;
      this.placeholderExampleIndex =
        (this.placeholderExampleIndex + 1) % this.placeholderExamples.length;
      delay = 350;
    }

    this.placeholderAnimationTimeout = setTimeout(
      () => this.animatePlaceholder(),
      delay,
    );
  }

  stopPlaceholderAnimation() {
    if (this.placeholderAnimationStopped) return;

    this.placeholderAnimationStopped = true;
    clearTimeout(this.placeholderAnimationTimeout);
    this.placeholderAnimationTimeout = undefined;
    if (this.input && !this.input.value) {
      this.input.placeholder = this.defaultPlaceholder;
    }
  }

  observeButtonState() {
    this.buttonStateObserver?.disconnect();

    this.buttonStateObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "disabled"
        ) {
          const isPersonalisationOpen =
            this.content && this.content.classList.contains("is-open");
          if (!isPersonalisationOpen) return;

          const isPersonalisationIncomplete =
            this.isPersonalisationIncomplete();

          if (isPersonalisationIncomplete && !this.addToCartBtn.disabled) {
            this.disabledInableBtn();
          }
        }
      });
    });

    this.buttonStateObserver.observe(this.addToCartBtn, {
      attributes: true,
      attributeFilter: ["disabled"],
    });
  }

  isPersonalisationIncomplete() {
    const hasPersonalisationFields = this.input && this.colors.length > 0;
    if (!hasPersonalisationFields) return false;

    if (this.isPackMode) {
      this.saveCurrentTabState();
      return this.getCompletedCount() < 1;
    }

    return this.inputValide.length === 0 || this.selectedColor.name == null;
  }

  openPersonalisation() {
    this.toggleOpenClose();
  }

  handleErrorMsg() {
    if (this.errorMsg) {
      this.errorMsg.removeAttribute("hidden");
      clearTimeout(this.errorMessageTimeout);
      this.errorMessageTimeout = setTimeout(() => {
        this.errorMessageTimeout = undefined;
        this.errorMsg?.setAttribute("hidden", "");
      }, 3000);
    }
  }

  cancelPersonalisation() {
    const isOpen = this.content?.classList.contains("is-open");
    if (this.isPackMode && isOpen) {
      this.cancelCurrentPackItem();
      return;
    }
    if (isOpen) {
      this.toggleOpenClose();
    } else {
      this.reset();
    }
  }

  measureContentHeight() {
    if (!this.content) return 0;

    const { maxHeight, overflow } = this.content.style;
    this.content.style.maxHeight = "none";
    this.content.style.overflow = "visible";
    const height = this.content.scrollHeight;
    this.content.style.maxHeight = maxHeight;
    this.content.style.overflow = overflow;
    return height;
  }

  updateContentMaxHeight() {
    if (!this.content?.classList.contains("is-open")) return;
    this.content.style.maxHeight = `${this.measureContentHeight()}px`;
  }

  bindContentImageLoadListeners() {
    if (!this.content || this.contentImageLoadBound) return;

    this.contentImageLoadHandler = (event) => {
      if (event.target.tagName !== "IMG") return;
      this.updateContentMaxHeight();
    };

    this.content.addEventListener("load", this.contentImageLoadHandler, true);
    this.content.addEventListener("error", this.contentImageLoadHandler, true);
    this.contentImageLoadBound = true;
  }

  unbindContentImageLoadListeners() {
    if (!this.content || !this.contentImageLoadBound) return;

    this.content.removeEventListener(
      "load",
      this.contentImageLoadHandler,
      true,
    );
    this.content.removeEventListener(
      "error",
      this.contentImageLoadHandler,
      true,
    );
    this.contentImageLoadBound = false;
    this.contentImageLoadHandler = undefined;
  }

  toggleOpenClose() {
    const isOpen = this.content.classList.contains("is-open");

    if (isOpen) {
      this.reset();
      this.unbindContentImageLoadListeners();
      this.content.style.maxHeight = null;
      this.content.style.overflow = "";
      this.content.style.display = "none";
      this.content.classList.remove("is-open");
      if (this.open) this.open.setAttribute("aria-expanded", "false");
      if (this.accordion) this.accordion.setAttribute("aria-expanded", "false");
      this.syncPersonalisationState();
    } else {
      const variantSelected =
        document.querySelector("input[type='radio'][name*='-']:checked") ||
        document.querySelector("input.input-radio__variant[checked]") ||
        document.querySelector("[data-variant-id]:checked") ||
        document.querySelector("input[type='radio']:checked");

      if (!variantSelected) {
        return this.handleErrorMsg();
      }

      this.initPackMode();

      this.content.style.display = "block";
      this.content.style.maxHeight = `${this.measureContentHeight()}px`;
      this.content.classList.add("is-open");
      this.bindContentImageLoadListeners();
      if (this.open) this.open.setAttribute("aria-expanded", "true");
      if (this.accordion) this.accordion.setAttribute("aria-expanded", "true");
      this.disabledInableBtn();

      this.el.scrollIntoView({ behavior: "smooth", block: "center" });
      this.syncPersonalisationState();
    }
  }

  disabledInableBtn() {
    if (!this.addToCartBtn) return;

    clearTimeout(this.buttonStateTimeout);
    this.buttonStateTimeout = undefined;

    const isPersonalisationOpen =
      this.content && this.content.classList.contains("is-open");

    if (!isPersonalisationOpen) {
      this.addToCartBtn.disabled = false;
      this.addToCartBtn.classList.remove("personalisation-required");
      this.addToCartBtn.removeAttribute("data-personalisation-message");
      return;
    }

    const hasPersonalisationFields = this.input && this.colors.length > 0;

    if (!hasPersonalisationFields) {
      this.addToCartBtn.disabled = false;
      this.addToCartBtn.classList.remove("personalisation-required");
      this.addToCartBtn.removeAttribute("data-personalisation-message");
      return;
    }

    let isValid = false;
    let message =
      theme?.personalizationStrings?.errorGeneral ||
      "Veuillez personnaliser votre produit";

    if (this.isPackMode) {
      this.saveCurrentTabState();
      const completedCount = this.getCompletedCount();
      isValid = completedCount >= 1;
      if (!isValid) {
        message =
          theme?.personalizationStrings?.packErrorAtLeastOne ||
          "Veuillez personnaliser au moins un article du pack";
      }
    } else if (this.inputValide.length > 0 && this.selectedColor.name != null) {
      isValid = true;
    } else {
      if (this.inputValide.length === 0 && this.selectedColor.name == null) {
        message =
          theme?.personalizationStrings?.errorMessageAndColor ||
          "Veuillez saisir un message\net choisir une couleur";
      } else if (this.inputValide.length === 0) {
        message =
          theme?.personalizationStrings?.errorMessage ||
          "Veuillez saisir un message personnalisé";
      } else if (this.selectedColor.name == null) {
        message =
          theme?.personalizationStrings?.errorColor ||
          "Veuillez choisir une couleur de broderie";
      }
    }

    if (isValid) {
      this.addToCartBtn.disabled = false;
      this.addToCartBtn.classList.remove("personalisation-required");
      this.addToCartBtn.removeAttribute("data-personalisation-message");
    } else {
      this.addToCartBtn.disabled = true;
      this.addToCartBtn.classList.add("personalisation-required");
      this.addToCartBtn.setAttribute("data-personalisation-message", message);

      this.buttonStateTimeout = setTimeout(() => {
        this.buttonStateTimeout = undefined;
        if (!this.addToCartBtn.disabled) {
          this.addToCartBtn.disabled = true;
          this.addToCartBtn.classList.add("personalisation-required");
          this.addToCartBtn.setAttribute(
            "data-personalisation-message",
            message,
          );
        }
      }, 100);
    }
  }

  addPicto(pictoBtn) {
    if (!this.input) return;

    const pictoEmoji = pictoBtn.dataset.pictoEmoji || "";
    const pictoValue = parseInt(pictoBtn.dataset.pictoNumber, 10) || 1;
    const currentTotal = this.calculateNbrOfCaracters(this.inputValide);
    const remainingCharacters = Math.max(this.maxNbr - currentTotal, 0);

    if (pictoValue > remainingCharacters) {
      this.showPictoLimitWarning(pictoValue, remainingCharacters);
      return;
    }

    this.hidePictoLimitWarning();
    this.input.value = this.inputValide + pictoEmoji;
    this.addValueInput();
  }

  showPictoLimitWarning(pictoValue, remainingCharacters) {
    if (!this.pictoWarning) return;

    const translatedTemplate = theme?.personalizationStrings?.pictoLimitWarning;
    const template =
      translatedTemplate &&
      !translatedTemplate.toLowerCase().includes("translation missing")
        ? translatedTemplate
        : "Cet emoji compte pour {{ count }} caractere(s). Il reste {{ remaining }} caractere(s).";

    const message = template
      .replace("{{ count }}", pictoValue)
      .replace("{{ remaining }}", remainingCharacters);

    if (this.pictoWarningText) {
      this.pictoWarningText.textContent = message;
    } else {
      this.pictoWarning.textContent = message;
    }

    this.pictoWarning.classList.remove("is-visible");
    this.pictoWarning.removeAttribute("hidden");
    void this.pictoWarning.offsetWidth;
    this.pictoWarning.classList.add("is-visible");
    this.updateContentMaxHeight();

    clearTimeout(this.pictoWarningTimeout);
    this.pictoWarningTimeout = setTimeout(() => {
      this.hidePictoLimitWarning();
    }, 7000);
  }

  hidePictoLimitWarning() {
    if (!this.pictoWarning) return;

    this.pictoWarning.classList.remove("is-visible");
    this.pictoWarning.setAttribute("hidden", "");
    if (this.pictoWarningText) this.pictoWarningText.textContent = "";
    clearTimeout(this.pictoWarningTimeout);
    this.pictoWarningTimeout = undefined;
    this.updateContentMaxHeight();
  }

  selectColor(colorInput) {
    this.selectedColor.id = colorInput.dataset.colorId;
    this.selectedColor.name = colorInput.dataset.colorName;

    if (this.colorLabel)
      this.colorLabel.textContent = this.selectedColor.name || "";

    if (this.isPackMode) {
      this.refreshPackState();
      return;
    }

    this.disabledInableBtn();
    this.create();
    this.updateBuyButtonPersonalisationSuffix();
  }

  addValueInput() {
    if (!this.input) return;

    if (this.input.value.includes("´")) {
      this.input.value = this.input.value.replaceAll("´", "'");
    }

    const input = this.input.value;
    const isValid = this.regex.test(input);
    const total = this.calculateNbrOfCaracters(input);

    if (isValid && total <= this.maxNbr) {
      this.inputValide = this.input.value;
    } else {
      this.input.value = this.inputValide;
    }

    this.checkNbrOfCaracters();

    if (this.isPackMode) {
      this.refreshPackState();
      return;
    }

    this.disabledInableBtn();
    this.create();
    this.updateBuyButtonPersonalisationSuffix();
  }

  calculateEmojiValue(input) {
    const emojiMatches = input.match(this.emojiPattern) || [];
    const emojiCount = {};
    let totalValue = 0;
    let regularCharacterCount = 0;

    this.emojis.forEach(({ emoji, value }) => {
      const count = emojiMatches.filter(
        (match) =>
          emoji.codePointAt(0).toString(16) ===
          match.codePointAt(0).toString(16),
      ).length;
      emojiCount[emoji] = count;
      totalValue += count * value;
    });

    for (const char of input) {
      if (this.charClassPattern.test(char)) {
        regularCharacterCount++;
      }
    }

    return { emojiCount, totalValue, regularCharacterCount };
  }

  calculateNbrOfCaracters(input) {
    const { totalValue, regularCharacterCount } =
      this.calculateEmojiValue(input);
    return totalValue + regularCharacterCount;
  }

  checkNbrOfCaracters() {
    if (!this.nbr) return;

    const total = this.calculateNbrOfCaracters(this.inputValide);
    const displayValue = total > this.maxNbr ? this.maxNbr : total;
    this.nbr.innerHTML = `${displayValue}`;
    this.nbrOfCaracters = displayValue;
  }

  remove() {
    const fakeInput = document.querySelector(".js-personalisation-input");
    if (fakeInput) {
      fakeInput.remove();
    }
    this.syncPersonalisationState();
  }

  handleVariantChange(event) {
    const isPersonalisationOpen = this.content?.classList.contains("is-open");
    const hasPackComposition =
      this.el.dataset.packComposition &&
      this.el.dataset.packComposition !== "{}";

    if (hasPackComposition) {
      if (event?.data?.variant?.id) {
        this.el.dataset.selectedVariantId = String(event.data.variant.id);
      }

      this.initPackMode();

      if (isPersonalisationOpen) {
        this.loadTabState(0);
        this.updateContentMaxHeight();
        this.unbindContentImageLoadListeners();
        this.bindContentImageLoadListeners();
        this.remove();
        this.disabledInableBtn();
        this.updatePriceSummary();
        this.updateBuyButtonPersonalisationSuffix();
      }
      return;
    }

    if (
      isPersonalisationOpen &&
      this.inputValide &&
      this.inputValide.length > 0 &&
      this.selectedColor.name != null &&
      this.customizationVariantId
    ) {
      this.create();
    }
  }

  create() {
    if (!this.customizationVariantId) return;

    if (this.isPackMode) {
      this.saveCurrentTabState();
      const completedItems = this.packItems.filter((item) =>
        this.isTabComplete(item),
      );
      if (completedItems.length === 0) {
        this.remove();
        return;
      }

      this.remove();

      const embroideryQuantity = completedItems.length;
      const basePriceInEuros = this.customizationPrice / 100;
      const customizationTotalInEuros = (
        basePriceInEuros * embroideryQuantity
      ).toFixed(2);

      const broderies = completedItems.map((item) => ({
        index: item.index,
        ean: item.ean,
        "Texte de broderie": item.text,
        "Couleur de fil": item.colorName,
      }));

      const customizationOptions = {
        __customization_total: customizationTotalInEuros,
        __customization_variant: this.customizationVariantId,
        _personalized_quantity: embroideryQuantity.toString(),
        // Préfixe __ : données structurées pour Cart Transform, non copiées en propriétés checkout
        __broderies: broderies,
      };

      const quantityLabel =
        theme?.personalizationStrings?.personalizedQuantity ||
        "Nombre de produits à broder";
      customizationOptions[quantityLabel] = embroideryQuantity.toString();

      // Propriétés plates (strings) pour l'affichage checkout sur la ligne Broderie
      completedItems.forEach((item) => {
        const label = this.getPackTabLabel(item.index);
        customizationOptions[`${label} - Texte de broderie`] = item.text;
        customizationOptions[`${label} - Couleur de fil`] = item.colorName;
      });

      if (completedItems.length === 1) {
        customizationOptions["Texte de broderie"] = completedItems[0].text;
        customizationOptions["Couleur de fil"] = completedItems[0].colorName;
      }

      this.appendVariantProperties(customizationOptions);

      const broderieValue = completedItems
        .map((item) => {
          const label = this.getPackTabLabel(item.index);
          return `${label} : ${item.text} / Couleur de broderie : ${item.colorName}`;
        })
        .join(" | ");

      this.injectCartProperties(
        customizationOptions,
        customizationTotalInEuros,
        broderieValue,
        embroideryQuantity,
      );
      return;
    }

    if (!this.selectedColor.id || !this.inputValide) return;

    this.remove();

    const basePriceInEuros = this.customizationPrice / 100;
    const customizationTotalInEuros = (
      basePriceInEuros * this.quantityToEmbroider
    ).toFixed(2);

    const customizationOptions = {
      __customization_total: customizationTotalInEuros,
      __customization_variant: this.customizationVariantId,
      "Couleur de fil": this.selectedColor.name,
      "Texte de broderie": this.inputValide,
    };

    if (this.unitCount > 1) {
      customizationOptions[
        theme?.personalizationStrings?.personalizedQuantity ||
          "Nombre de produits à broder"
      ] = this.quantityToEmbroider.toString();
      customizationOptions["_personalized_quantity"] =
        this.quantityToEmbroider.toString();
    }

    this.appendVariantProperties(customizationOptions);

    const broderieValue = `${this.inputValide} / Couleur de broderie : ${this.selectedColor.name}`;

    this.injectCartProperties(
      customizationOptions,
      customizationTotalInEuros,
      broderieValue,
      this.unitCount > 1 ? this.quantityToEmbroider : null,
    );
  }

  injectCartProperties(
    customizationOptions,
    customizationTotalInEuros,
    broderieValue,
    embroideryQuantity,
  ) {
    const element = document.createElement("div");
    element.classList.add("js-personalisation-input");

    const appendHiddenInput = (name, value) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      input.value = value == null ? "" : String(value);
      element.appendChild(input);
    };

    appendHiddenInput("properties[Broderie]", broderieValue);
    appendHiddenInput("properties[_is_personalized]", "true");
    appendHiddenInput(
      "properties[__customization_variant]",
      this.customizationVariantId,
    );
    appendHiddenInput(
      "properties[__customization_total]",
      customizationTotalInEuros,
    );
    appendHiddenInput(
      "properties[__customization_options]",
      JSON.stringify(customizationOptions),
    );

    if (embroideryQuantity != null && embroideryQuantity > 0) {
      const quantityLabel =
        theme?.personalizationStrings?.personalizedQuantity ||
        "Nombre à broder";
      appendHiddenInput(
        `properties[${quantityLabel}]`,
        embroideryQuantity,
      );
      appendHiddenInput(
        "properties[_personalized_quantity]",
        embroideryQuantity,
      );
    }

    const form = this.getProductForm();
    if (form) {
      form.appendChild(element);
    }
    this.syncPersonalisationState();
  }

  reinit() {
    this.init();
  }

  resetPackItems() {
    if (!this.packItems.length) return;

    this.packItems.forEach((item) => {
      item.text = "";
      item.colorId = null;
      item.colorName = null;
    });
    this.activeTabIndex = 0;
    this.completedCount = 0;
  }

  resetFields() {
    clearTimeout(this.resetFieldsTimeout);
    this.resetFieldsTimeout = undefined;

    if (this.input) {
      this.input.value = "";
      this.input.setAttribute("autocomplete", "off");
      this.input.setAttribute("autocomplete", "new-password");
    }

    if (this.colors.length > 0) {
      this.colors.forEach((colorInput) => {
        colorInput.checked = false;
      });
    }
    if (this.colorLabel) this.colorLabel.textContent = "";

    if (this.quantitySelect) {
      this.quantitySelect.value = "1";
      this.quantityToEmbroider = 1;
    }

    if (this.nbr) {
      this.nbr.innerHTML = "0";
    }

    this.inputValide = "";
    this.selectedColor.id = null;
    this.selectedColor.name = null;
    this.nbrOfCaracters = 0;

    this.resetPackItems();
    if (this.isPackMode) {
      this.loadTabState(0);
      this.updateTabsUI();
      this.updatePriceSummary();
    }

    this.remove();

    this.resetFieldsTimeout = setTimeout(() => {
      this.resetFieldsTimeout = undefined;
      if (this.input && this.input.value !== "") {
        this.input.value = "";
        this.inputValide = "";
        if (this.nbr) {
          this.nbr.innerHTML = "0";
        }
        this.nbrOfCaracters = 0;
      }
      this.syncPersonalisationState();
    }, 500);

    this.syncPersonalisationState();
  }

  reset() {
    if (this.input) {
      this.input.value = "";
    }
    this.inputValide = "";

    if (this.colors.length > 0) {
      this.colors.forEach((colorInput) => {
        colorInput.checked = false;
      });
    }
    this.selectedColor.id = null;
    this.selectedColor.name = null;
    if (this.colorLabel) this.colorLabel.textContent = "";

    if (this.quantitySelect) {
      this.quantitySelect.value = "1";
      this.quantityToEmbroider = 1;
    }

    if (this.nbr) {
      this.nbr.innerHTML = "0";
    }
    this.nbrOfCaracters = 0;

    this.resetPackItems();
    if (this.isPackMode) {
      this.loadTabState(0);
      this.updateTabsUI();
      this.updatePriceSummary();
    }

    if (this.addToCartBtn) {
      this.addToCartBtn.disabled = false;
      this.addToCartBtn.classList.remove("personalisation-required");
      this.addToCartBtn.removeAttribute("data-personalisation-message");
    }

    this.remove();
    this.syncPersonalisationState();
  }
}

document.addEventListener("DOMContentLoaded", () => {
  window.productPersonalisationInstance = new ProductPersonalisation();
});

document.addEventListener("product-info:updated", () => {
  if (window.productPersonalisationInstance) {
    window.productPersonalisationInstance.init();
  }
});

window.ProductPersonalisation = ProductPersonalisation;
